import { useEffect, useState } from "react";
import { useMediaQuery } from "@mui/material";
import { useCart } from "@ecommerce/store";
import { trackAddToCart } from "@ecommerce/analytics";
import { productQuantity, type ProductQuantityValidationError } from "@ecommerce/shared-utils";
import { useSnackbar } from "notistack";
import { formatMoney } from "../../styles/liquidGlass";
import { Icon } from "../liquid/LiquidPrimitives";

interface QuantitySelectorProps {
    productId: string;
    variantId?: string;
    productName: string;
    productPrice: number;
    productImage: string;
    quantity: number;
    stock: number | null;
    unitMeasureCode: number;
    cantDecimal: number;
    unitLabel: string;
    onQuantityChange: (newQuantity: number) => void;
    minQuantity?: number;
    onAfterAddToCart?: () => void;
    attributes?: { name: string; value: string }[];
}

export default function QuantitySelector({
    productId,
    variantId,
    productName,
    productPrice,
    productImage,
    quantity,
    stock,
    unitMeasureCode,
    cantDecimal,
    unitLabel,
    onQuantityChange,
    minQuantity,
    onAfterAddToCart,
    attributes,
}: QuantitySelectorProps) {
    const isMobile = useMediaQuery("(max-width:768px)");
    const { addToCart, isCartOpen, toggleCart } = useCart();
    const { enqueueSnackbar } = useSnackbar();

    const quantityRules = {
        cantDecimal,
        storefrontMinimum: minQuantity,
        stock,
    };
    const effectiveMinQuantity = productQuantity.getMinimum(quantityRules);
    const isPurchasable = stock === null || stock >= effectiveMinQuantity;
    const formatQuantity = (value: number) => productQuantity.format(value, cantDecimal);
    const [inputValue, setInputValue] = useState(() => formatQuantity(quantity));

    useEffect(() => {
        setInputValue(formatQuantity(quantity));
    }, [cantDecimal, quantity]);

    const showValidationError = (
        error: ProductQuantityValidationError | "parse",
        rawValue: string,
    ) => {
        const numericValue = Number(rawValue.trim().replace(",", "."));
        let message = "Ingresá una cantidad válida";

        if (Number.isFinite(numericValue) && numericValue <= 0) {
            message = "La cantidad debe ser mayor a cero";
        } else if (error === "invalid-precision" || (error === "parse" && cantDecimal === 0)) {
            message = "Este producto sólo admite cantidades enteras";
        } else if (error === "below-minimum") {
            message = `La cantidad mínima es ${formatQuantity(effectiveMinQuantity)} ${unitLabel}`;
        } else if (error === "above-stock" && stock !== null) {
            message = `Hay como máximo ${formatQuantity(stock)} ${unitLabel}`;
        }

        enqueueSnackbar(message, { variant: "warning" });
    };

    const confirmInput = (): number | null => {
        const parsedQuantity = productQuantity.parseInput(inputValue, cantDecimal);
        if (parsedQuantity == null) {
            showValidationError("parse", inputValue);
            setInputValue(formatQuantity(quantity));
            return null;
        }

        const normalizedQuantity = productQuantity.normalize(parsedQuantity, cantDecimal);
        const validation = productQuantity.validate(normalizedQuantity, quantityRules);
        if (!validation.valid) {
            showValidationError(validation.error ?? "parse", inputValue);
            setInputValue(formatQuantity(quantity));
            return null;
        }

        onQuantityChange(normalizedQuantity);
        setInputValue(formatQuantity(normalizedQuantity));
        return normalizedQuantity;
    };

    const updateFromButton = (newQuantity: number) => {
        onQuantityChange(newQuantity);
        setInputValue(formatQuantity(newQuantity));
    };

    const handleAddToCart = async () => {
        const confirmedQuantity = confirmInput();
        if (confirmedQuantity == null) return;

        if (!isPurchasable) {
            enqueueSnackbar(
                `No hay stock suficiente para el mínimo requerido (${formatQuantity(effectiveMinQuantity)} ${unitLabel})`,
                { variant: "warning" },
            );
            return;
        }
        if (!variantId) {
            enqueueSnackbar("Por favor selecciona una variante del producto", {
                variant: "warning",
            });
            return;
        }

        await addToCart({
            idProduct: productId,
            idProductVariant: variantId,
            name: productName,
            price: productPrice,
            image: productImage,
            stock: stock ?? undefined,
            unitMeasureCode,
            unitLabel,
            cantDecimal,
            minimumQuantity: effectiveMinQuantity,
            quantity: confirmedQuantity,
            attributes: attributes || [],
        });
        trackAddToCart(variantId, productPrice);
        if (!isCartOpen) toggleCart();
        onAfterAddToCart?.();
    };

    return (
        <div
            className="lg-strong"
            style={{ padding: isMobile ? "16px 16px" : "20px 24px", borderRadius: 22 }}
        >
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 14,
                }}
            >
                <div className="eyebrow">Cantidad</div>
                <span className="mono" style={{ fontSize: 11, color: "var(--ink-500)" }}>
                    {stock === null
                        ? "Disponible"
                        : `${formatQuantity(stock)} ${unitLabel.toUpperCase()} EN STOCK`}
                </span>
            </div>

            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: isMobile ? 8 : 18,
                }}
            >
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        padding: 4,
                        borderRadius: 999,
                        background: "rgba(10,10,10,0.06)",
                    }}
                >
                    <button
                        type="button"
                        onClick={() =>
                            updateFromButton(productQuantity.decrement(quantity, quantityRules))
                        }
                        disabled={quantity <= effectiveMinQuantity}
                        style={{
                            width: 38,
                            height: 38,
                            borderRadius: 999,
                            border: "none",
                            cursor: quantity <= effectiveMinQuantity ? "not-allowed" : "pointer",
                            background: "white",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            opacity: quantity <= effectiveMinQuantity ? 0.45 : 1,
                        }}
                    >
                        <Icon.minus />
                    </button>
                    <input
                        className="mono"
                        type="text"
                        inputMode={cantDecimal === 0 ? "numeric" : "decimal"}
                        step={productQuantity.getInputStep(cantDecimal)}
                        min={effectiveMinQuantity}
                        max={stock ?? undefined}
                        aria-label="Cantidad"
                        value={inputValue}
                        onChange={(event) => setInputValue(event.target.value)}
                        onBlur={confirmInput}
                        onKeyDown={(event) => {
                            if (event.key === "Enter") {
                                event.preventDefault();
                                event.currentTarget.blur();
                            } else if (event.key === "Escape") {
                                event.preventDefault();
                                setInputValue(formatQuantity(quantity));
                            }
                        }}
                        style={{
                            minWidth: isMobile ? 36 : 50,
                            width: isMobile ? 52 : 64,
                            textAlign: "center",
                            fontSize: 16,
                            fontWeight: 600,
                            border: "none",
                            outline: "none",
                            background: "transparent",
                            color: "inherit",
                        }}
                    />
                    <button
                        type="button"
                        onClick={() =>
                            updateFromButton(productQuantity.increment(quantity, quantityRules))
                        }
                        disabled={!isPurchasable || (stock !== null && quantity >= stock)}
                        style={{
                            width: 38,
                            height: 38,
                            borderRadius: 999,
                            border: "none",
                            cursor:
                                !isPurchasable || (stock !== null && quantity >= stock)
                                    ? "not-allowed"
                                    : "pointer",
                            background: "white",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            opacity:
                                !isPurchasable || (stock !== null && quantity >= stock) ? 0.45 : 1,
                        }}
                    >
                        <Icon.plus />
                    </button>
                </div>

                <div style={{ textAlign: "right", minWidth: 0, flexShrink: 0 }}>
                    <div
                        className="mono"
                        style={{ fontSize: 11, color: "var(--ink-500)", letterSpacing: "0.1em" }}
                    >
                        SUBTOTAL
                    </div>
                    <div
                        className="mono"
                        style={{
                            fontSize: isMobile ? 18 : 22,
                            fontWeight: 600,
                            marginTop: 2,
                            whiteSpace: "nowrap",
                        }}
                    >
                        {formatMoney(quantity * productPrice)}
                    </div>
                </div>
            </div>

            {minQuantity != null && minQuantity > 1 && (
                <div
                    style={{
                        marginTop: 14,
                        padding: "10px 14px",
                        borderRadius: 12,
                        background: "rgba(217, 194, 162, 0.25)",
                        border: "1px solid rgba(217, 194, 162, 0.5)",
                        fontSize: 12,
                        color: "var(--ink-700)",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                    }}
                >
                    <Icon.spark />
                    Mínimo de compra{" "}
                    <strong style={{ fontWeight: 600 }}>
                        {formatQuantity(effectiveMinQuantity)} {unitLabel}
                    </strong>
                </div>
            )}

            <button
                type="button"
                className="btn-primary"
                onMouseDown={(event) => event.preventDefault()}
                onClick={handleAddToCart}
                disabled={!isPurchasable || !variantId}
                style={{
                    width: "100%",
                    marginTop: 14,
                    padding: "20px 28px",
                    fontSize: 15,
                    opacity: !isPurchasable || !variantId ? 0.55 : 1,
                    cursor: !isPurchasable || !variantId ? "not-allowed" : "pointer",
                }}
            >
                <Icon.bag /> Agregar al carrito
            </button>
        </div>
    );
}
